import {t, Trans} from '@lingui/macro';
import {capitalize} from 'lodash';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, HandlerOf, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {computeCustomFieldIconName, renderCustomFieldType} from '../../../helpers/customFieldViewHelpers';
import {CustomFieldBaseModel, CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsFooter, StyledSettingsMain, StyledSettingsSidebar} from '../../common/settingsWrapper';
import {settingsHorizontalPaddingStyles} from '../../settingsCommonConstants';
import {getCustomFieldsBaseUrl} from '../customFieldsHelpers';
import {CustomFieldBreadcrumb} from './customFieldBreadcrumb';
import {CustomFieldEditSidebar} from './customFieldEditSidebar';

/*
 * Constants.
 */

export const InputWidth = 248;
export const TextareaWidth = 480;
const dropdownWidth = 180;
const availableNewCustomFieldTypes = [
  CustomFieldsEnum.DATETIME,
  CustomFieldsEnum.INBOX,
  CustomFieldsEnum.NUMBER,
  CustomFieldsEnum.TEAMMATE,
  CustomFieldsEnum.STRING,
  CustomFieldsEnum.BOOLEAN,
];

/*
 * Types.
 */

interface CustomFieldFormProps {
  tabPage: SettingsCustomFieldsPagesEnum;
  customField?: CustomFieldModel;
  errors: Record<string, ReactNode>;
  isEditing?: boolean;

  renderActions?: Renderer;
  setErrors: HandlerOf<Record<string, ReactNode>>;
  onSubmit: AsyncHandlerOf<CustomFieldBaseModel>;
}

interface ComputeCanSaveOptions {
  customFieldName: string;
  nameTextboxValue: string;
  customFieldDescription: string;
  descriptionTextboxValue: string;
  isSaving: boolean;
  isEditing?: boolean;
}

/*
 * Style.
 */

export const StyledCustomFieldFormWrapperDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  position: relative;
`;

export const StyledSettingsContentWrap = styled.div`
  flex: 1;
  margin-bottom: 48px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
  max-width: 650px;
`;

export const StyledSettingsContent = styled.div`
  flex: 1;
  min-height: min-content;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 22px;
`;

export const StyledSettingsField = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledInputWrap = styled.div`
  width: ${InputWidth}px;
`;

const StyledTypeInputContainerDiv = styled.div`
  display: flex;
`;

const StyledTextAreaBoxWrap = styled.div`
  width: ${TextareaWidth}px;
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  width: ${TextareaWidth}px;
`;

const StyledTextLabel = styled.label<{$isDisabled?: boolean}>`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${boxedInputColorReaders.textLabel};
  opacity: ${(p) => (p.$isDisabled ? '0.5' : '1')};
  grid-area: title;
  margin-bottom: -4px;
`;

const StyledSettingsSidebarWrap = styled.div`
  margin: 0 0 0 auto;
`;

const StyledCustomFieldContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledCustomFieldNameDiv = styled.div`
  flex: 1;
`;

const StyledIcon = styled(Icon)`
  margin-top: 2px;
`;

/*
 * Component.
 */

export const CustomFieldForm: FC<CustomFieldFormProps> = ({
  customField,
  tabPage,
  errors,
  isEditing = false,
  onSubmit,
  setErrors,
  renderActions,
}) => {
  const {navigateToLink} = useLinkNavigation();
  const localize = useLocalize();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(customField?.name ?? '');
  const [description, setDescription] = useState(customField?.description ?? '');
  const [type, setType] = useState(customField?.type ?? CustomFieldsEnum.STRING);

  const canSave = computeCanSave({
    customFieldName: customField?.name ?? '',
    customFieldDescription: customField?.description ?? '',
    nameTextboxValue: name,
    descriptionTextboxValue: description,
    isSaving,
    isEditing,
  });

  const onSubmitClick = async () => {
    setIsSaving(true);
    try {
      await onSubmit({name, description, type});
    } catch {
      setIsSaving(false);
    }
  };

  const onCancelClick = () => {
    navigateToLink(`${getCustomFieldsBaseUrl(tabPage)}/overview`);
  };

  const renderFooter = () => (
    <SettingsFooter>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
        <Trans id="settings-custom-fields-cancel">Cancel</Trans>
      </Button>
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSubmitClick} isDisabled={!canSave}>
        {isEditing ? (
          <Trans id="settings-custom-fields-savetag">Save</Trans>
        ) : (
          <Trans id="settings-custom-fields-createtag">Create</Trans>
        )}
      </Button>
    </SettingsFooter>
  );

  const maybeRenderSidebar = () => {
    if (!isEditing || !customField) {
      return null;
    }

    return (
      <StyledSettingsSidebarWrap>
        <StyledSettingsSidebar>
          <CustomFieldEditSidebar customField={customField} />
        </StyledSettingsSidebar>
      </StyledSettingsSidebarWrap>
    );
  };

  return (
    <StyledCustomFieldFormWrapperDiv>
      <CustomFieldBreadcrumb
        tabPage={tabPage}
        title={isEditing ? capitalize(customField?.name) : customFieldBreadcrumbHeaderMap[tabPage]}
        renderActions={renderActions}
      />
      <StyledSettingsMain>
        <StyledSettingsContentWrap>
          <StyledSettingsContent>
            <StyledSettingsField>
              <StyledInputWrap>
                <TextBox
                  label={<Trans id="custom-field-editor-name">Name</Trans>}
                  value={name}
                  onChange={(n) => {
                    setName(n);
                    setErrors({...errors, name: undefined});
                  }}
                  isRequired
                  shouldAutoFocus
                  hasError={errors.name !== undefined}
                  errorMessage={errors.name}
                />
              </StyledInputWrap>
            </StyledSettingsField>

            <StyledSettingsField>
              <StyledTextAreaBoxWrap>
                <StyledTextAreaBox
                  label={<Trans id="custom-field-editor-description">Description</Trans>}
                  value={description}
                  boxStyles={{height: '70px'}}
                  onChange={(n) => {
                    setDescription(n);
                    setErrors({...errors, description: undefined});
                  }}
                  hasError={errors.description !== undefined}
                  errorMessage={errors.description}
                  placeholder={localize(
                    t('custom-field-editor-description-placeholder')`What will this custom field be about?`,
                  )}
                />
              </StyledTextAreaBoxWrap>
            </StyledSettingsField>

            <StyledSettingsField>
              <StyledTextLabel $isDisabled={isEditing}>
                <Trans id="custom-field-editor-type">Type</Trans>
              </StyledTextLabel>

              <StyledTypeInputContainerDiv>
                <TooltipCoordinator
                  render={
                    isEditing
                      ? () => (
                          <Trans id="custom-field-editor-type-tooltip">
                            Cannot edit type after custom field is created
                          </Trans>
                        )
                      : undefined
                  }
                >
                  <DropdownBoxCoordinator
                    isDisabled={isEditing}
                    value={renderCustomField(type)}
                    renderDropdown={(popoverProps) => renderDropdown(popoverProps, setType, type)}
                  />
                </TooltipCoordinator>
              </StyledTypeInputContainerDiv>
            </StyledSettingsField>
          </StyledSettingsContent>
        </StyledSettingsContentWrap>
        {maybeRenderSidebar()}
      </StyledSettingsMain>
      {renderFooter()}
    </StyledCustomFieldFormWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  setType: HandlerOf<CustomFieldsEnum>,
  type: CustomFieldsEnum,
) {
  const items = availableNewCustomFieldTypes.map((option) => (
    <SimpleDropdownItem key={option} onSelect={() => setType(option)} isChecked={type === option}>
      {renderCustomField(option)}
    </SimpleDropdownItem>
  ));

  return (
    <SimpleDropdown {...popoverProps} width={dropdownWidth}>
      {items}
    </SimpleDropdown>
  );
}

function renderCustomField(type: CustomFieldsEnum) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledCustomFieldContainerDiv>
          <StyledIcon
            name={computeCustomFieldIconName(type, VisualSizesEnum.SMALL)}
            color={theme.greys.shade70}
          />
          <StyledCustomFieldNameDiv>{renderCustomFieldType(type)}</StyledCustomFieldNameDiv>
        </StyledCustomFieldContainerDiv>
      )}
    </UserThemeConsumer>
  );
}

const customFieldBreadcrumbHeaderMap = {
  [SettingsCustomFieldsPagesEnum.TEAMMATES]: (
    <Trans id="custom-field-editor-breadcrumb-header-teammate">Add teammate custom field</Trans>
  ),
  [SettingsCustomFieldsPagesEnum.INBOXES]: (
    <Trans id="custom-field-editor-breadcrumb-header-inbox">Add inbox custom field</Trans>
  ),
  [SettingsCustomFieldsPagesEnum.CARDS]: (
    <Trans id="custom-field-editor-breadcrumb-header-contact">Add contact custom field</Trans>
  ),
  [SettingsCustomFieldsPagesEnum.ACCOUNTS]: (
    <Trans id="custom-field-editor-breadcrumb-header-account">Add account custom field</Trans>
  ),
};

function computeCanSave(options: ComputeCanSaveOptions) {
  const {
    customFieldName,
    nameTextboxValue,
    customFieldDescription,
    descriptionTextboxValue,
    isSaving,
    isEditing,
  } = options;

  // If we are saving, the save button should be disabled.
  if (isSaving) {
    return false;
  }
  // If we are creating a new custom field, only a name is required.
  if (!isEditing) {
    return Boolean(nameTextboxValue);
  }
  // If we are editing, only allow saving if a value has changed.
  return customFieldName !== nameTextboxValue || customFieldDescription !== descriptionTextboxValue;
}
